import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { SectionCard } from "@/components/ui/section-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Server, Settings2, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/auth";
import { projectsApi } from "@/lib/api";
import { useTranslation } from "react-i18next";

const ENVIRONMENTS = {
  prod: { label: "PROD", url: "prod" },
  beta: { label: "BETA", url: "beta" },
  custom: { label: "Custom", url: "" },
};

type EnvironmentKey = keyof typeof ENVIRONMENTS;

const ApiTesting = () => {
  const { t } = useTranslation('apiTesting');
  const [environment, setEnvironment] = useState<EnvironmentKey>("beta");
  const [customUrl, setCustomUrl] = useState("");
  const [timeoutValue, setTimeoutValue] = useState("90000");
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<"idle" | "success" | "error">("idle");

  useEffect(() => {
    const savedConfig = localStorage.getItem("api_config");
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        if (config.timeout) setTimeoutValue(config.timeout.toString());
        
        if (config.baseUrl === ENVIRONMENTS.prod.url) {
          setEnvironment("prod");
        } else if (config.baseUrl === ENVIRONMENTS.beta.url) {
          setEnvironment("beta");
        } else if (config.baseUrl) {
          setEnvironment("custom");
          setCustomUrl(config.baseUrl);
        }
      } catch (e) {
        console.error("Failed to parse saved API config:", e);
      }
    }
  }, []);

  const getCurrentUrl = (): string => {
    if (environment === "custom") {
      return customUrl;
    }
    return ENVIRONMENTS[environment].url;
  };

  const handleSaveConfig = () => {
    const baseUrl = getCurrentUrl();
    localStorage.setItem("api_config", JSON.stringify({ 
      baseUrl, 
      timeout: parseInt(timeoutValue),
      environment 
    }));
    
    window.dispatchEvent(new CustomEvent('api-config-changed', { detail: { baseUrl } }));
    
    toast({
      title: t('saved'),
      description: t('savedDescription', { env: ENVIRONMENTS[environment]?.label || 'Custom', url: baseUrl }),
    });
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setConnectionStatus("idle");

    const baseUrl = getCurrentUrl();
    
    try {
      localStorage.setItem("api_config", JSON.stringify({ 
        baseUrl, 
        timeout: parseInt(timeoutValue),
        environment 
      }));

      const response = await authFetch(
        projectsApi('action=list_collections')
      );

      setIsTesting(false);
      
      if (response.ok) {
        setConnectionStatus("success");
        toast({
          title: t('connected'),
          description: t('connectedDescription', { url: baseUrl }),
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setConnectionStatus("error");
        toast({
          title: t('connectionError'),
          description: errorData.error || t('connectionErrorApi', { status: response.status }),
          variant: "destructive",
        });
      }
    } catch (error) {
      setIsTesting(false);
      setConnectionStatus("error");
      toast({
        title: t('connectionError'),
        description: t('connectionErrorGeneric', { error: error instanceof Error ? error.message : 'Unknown error' }),
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout>
      <SectionCard
        icon={Server}
        title={t('title')}
        description={t('description')}
      >
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-sm">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{t('configuration')}</span>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-semibold">{t('apiConfiguration')}</h3>

            <div className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="environment">{t('environment')}</Label>
                <Select value={environment} onValueChange={(value: EnvironmentKey) => setEnvironment(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectEnvironment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prod">
                      <span className="font-medium text-green-600">PROD</span>
                    </SelectItem>
                    <SelectItem value="beta">
                      <span className="font-medium text-yellow-600">BETA</span>
                    </SelectItem>
                    <SelectItem value="custom">
                      <span className="font-medium text-blue-600">Custom</span> - {t('customUrl')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {environment === "custom" && (
                <div className="space-y-2">
                  <Label htmlFor="customUrl">{t('customUrl')}</Label>
                  <Input
                    id="customUrl"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="http://localhost:8003"
                  />
                </div>
              )}

              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>{t('currentUrl')}</strong>{" "}
                  <code className="rounded bg-background px-2 py-1">{getCurrentUrl()}</code>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeout">{t('timeout')}</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={timeoutValue}
                  onChange={(e) => setTimeoutValue(e.target.value)}
                  placeholder="90000"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button onClick={handleSaveConfig}>
                  {t('saveConfig')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  {isTesting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t('testConnection')}
                </Button>
              </div>

              {connectionStatus !== "idle" && (
                <div
                  className={`mt-4 flex items-center gap-2 rounded-lg p-3 ${
                    connectionStatus === "success"
                      ? "bg-green-500/10 text-green-600"
                      : "bg-destructive/10 text-destructive"
                  }`}
                >
                  {connectionStatus === "success" ? (
                    <>
                      <CheckCircle2 className="h-5 w-5" />
                      <span>{t('connectionSuccess')}</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5" />
                      <span>{t('connectionFailed')}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </SectionCard>
    </MainLayout>
  );
};

export default ApiTesting;
